import React, { useCallback, useContext, useEffect, useState } from "react";
import { commitMutation, graphql, useFragment } from "react-relay";

import { faCheck, faSpinnerThird, faUserPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact, escapeRegExp, toLower } from "lodash";
import uuidv4 from "uuid/v4";

import { Input } from "~/auth/components";
import ViewportContext from "~/common/ViewportContext";
import { useNewOnboardingFlow } from "~/lib/features";
import recaptcha from "~/lib/recaptcha";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsPage, dispatchAnalyticsTrack } from "~/lib/segment";
import { emailGreylist, getFirstUserFacingError, listFormat, nameFromEmailEntry } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import { ButtonLink } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import avatarsLeft from "./images/avatars-left.svg?url";
import avatarsRight from "./images/avatars-right.svg?url";
import { JourneyContext } from "./JourneyContext";

import type {
	InvitePage_viewer$data as Viewer,
	InvitePage_viewer$key as ViewerKey,
} from "~/__generated__/InvitePage_viewer.graphql";
import type { InvitePageContactMutation as MutationType } from "~/__generated__/InvitePageContactMutation.graphql";
import type { InvitePageQuery as QueryType } from "~/__generated__/InvitePageQuery.graphql";

import stylesOnboarding from "~/onboarding/css/Onboarding.module.css";

import styles from "./css/InvitePage.module.css";

export const INVITE_PATH = "/o/invite/";

const InvitePageContactMutation = graphql`
	mutation InvitePageContactMutation($input: InviteUsersMutationInput!) {
		inviteUsers(input: $input) {
			invitees {
				errors {
					field
					message
				}
			}
		}
	}
`;

export interface InviteeType {
	id: string;
	email: string;
	image?: string;
	error?: string;
}

export type Domain = Viewer["account"]["domains"][0];

interface InviteInputProps {
	invite: InviteeType;
	domains: string[];
	regex: RegExp;
	onTextChange: (id: string, email: string) => void;
	className?: string;
	onRemove?: (id: string) => void;
}

export function InviteInput(props: InviteInputProps) {
	const { onTextChange: onTextChangeProp, onRemove: onRemoveProp } = props;

	const onChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			return onTextChangeProp(props.invite.id, e.target.value);
		},
		[onTextChangeProp, props.invite.id],
	);

	const onRemove = useCallback(() => {
		return onRemoveProp?.(props.invite.id);
	}, [onRemoveProp, props.invite.id]);

	const onClear = useCallback(() => {
		return onTextChangeProp(props.invite.id, "");
	}, [onTextChangeProp, props.invite.id]);

	let { email, error } = props.invite;

	return (
		<div className={cx(styles.inviteRow, props.className)}>
			<div className={styles.inviteInputContainer}>
				<Input
					profile
					value={email}
					pattern={props.regex.source}
					placeholder={props.domains.length > 0 ? `name@${props.domains[0]}` : "name@example.com"}
					onChange={onChange}
					onClear={onClear}
					onRemove={onRemoveProp ? onRemove : undefined}
					className={styles.inviteInput}
				/>
				{error && <span className={styles.inviteError}>{error}</span>}
			</div>
		</div>
	);
}

type InvitePageProps = {
	viewer: ViewerKey;
	invites: InviteeType[];
	onNext: () => void;
};

interface InvitePageAnalytics {
	numInvites: number;
	numInitialInvitesNotIncluded: number;
	numInitialInvites: number;
	syncWasReady: boolean;
}

export interface Invitation {
	email: string;
	fullName: string;
	sendInviteEmail: boolean | null;
}

function InvitePage(props: InvitePageProps) {
	const [invites, setInvites] = useState<InviteeType[]>([]);
	const [invitesSent, setInvitesSent] = useState<boolean>(false);
	const [invitesSuccess, setInvitesSuccess] = useState<boolean>(false);
	const { isMobile } = useContext(ViewportContext);
	const isNewOnboardingFlow = useNewOnboardingFlow();

	const { questionAnswers, user, account, syncReady } = useFragment(
		graphql`
			fragment InvitePage_viewer on Viewer {
				questionAnswers
				syncReady
				user {
					id
					fullName
					email
					internalAttributes {
						isAccountCreator
					}
				}
				account {
					domains {
						domain
						aliases
					}
				}
			}
		`,
		props.viewer,
	);

	// For the variant flow, always use non-manager version
	// For the control flow, use the value from questionAnswers
	const isManager = isNewOnboardingFlow ? false : questionAnswers.is_manager;

	const { interest } = questionAnswers;
	const domains: string[] = (account.domains as Domain[]).reduce((acc: string[], domain: Domain) => {
		return domain ? [...acc, domain.domain, ...domain.aliases] : acc;
	}, []);

	const pattern = `^[^@]+@(${domains.map(escapeRegExp).join("|")})$`;
	const regex = domains.length > 0 ? new RegExp(pattern, "i") : new RegExp("^[^@]+@[^@]+\\.[a-zA-Z]+$");

	useEffect(() => {
		const prefillCount = user.internalAttributes?.isAccountCreator ? 2 : 4;

		let propInvites = props.invites.slice(0, prefillCount);
		propInvites = new Array(prefillCount + 1).fill(0).map((_item, i) => {
			return propInvites[i] ?? { id: uuidv4(), email: "" };
		});

		dispatchAnalyticsPage("Onboarding", "Invite", {
			numInvites: Math.min(propInvites.length, prefillCount + 1),
			syncWasReady: !!syncReady,
		});

		setInvites(propInvites);
	}, [user, props.invites, syncReady]);

	const getInviteAnalytics = useCallback((): InvitePageAnalytics => {
		const invitesFromProps = props.invites.filter(invite => invite.email);
		const invitesFromState = invites.filter(invite => invite.email);

		return {
			syncWasReady: !!syncReady,
			numInvites: invitesFromState.length,
			numInitialInvitesNotIncluded: invitesFromProps.filter(invite => invitesFromState.includes(invite)).length,
			numInitialInvites: invitesFromProps.length,
		};
	}, [props.invites, invites, syncReady]);

	const onInputChange = useCallback((id: string, email: string) => {
		setInvites(invites =>
			invites.map(invite => {
				if (invite.id === id) return { ...invite, error: "", email };
				return invite;
			}),
		);
	}, []);

	const insertInput = useCallback(() => {
		setInvites(invites => [...invites, { id: uuidv4(), email: "" }]);
	}, []);

	const removeInput = useCallback((id: string) => {
		setInvites(invites => invites.filter(invite => invite.id !== id));
	}, []);

	const areValid = useCallback((): boolean => {
		let valid = true;

		// Check for email format errors
		const emailRegex =
			domains.length > 0
				? new RegExp(`^[^@]+@(${domains.map(escapeRegExp).join("|")})$`, "i")
				: new RegExp("^[^@]+@[^@]+\\.[a-zA-Z]+$");

		setInvites(
			invites.map(invite => {
				const sameEmailAsUser = toLower(invite.email) === toLower(user.email);
				if ((!invite.email || emailRegex.test(invite.email)) && !sameEmailAsUser) return invite;

				valid = false;
				const error = sameEmailAsUser
					? `Must be an email different than ${user.email}`
					: domains.length > 0
						? `Must be a valid email ending in: ${listFormat(domains, { sep: ", ", last: " or " }).join("")}`
						: "Invalid email address";
				return { ...invite, error };
			}),
		);

		return valid;
	}, [domains, invites, user.email]);

	const { onNext } = props;

	const addTeammates = useCallback(async () => {
		if (!areValid()) return;
		if (invitesSent) return;

		setInvitesSent(true);

		const invitations: Invitation[] = invites
			.filter(invite => invite.email)
			.map(({ email }) => {
				return {
					email: email,
					fullName: nameFromEmailEntry(email),
					sendInviteEmail: true,
					// Always use "TEAMMATE" relationship for the variant
					relationship: isManager && !isNewOnboardingFlow ? "SUBORDINATE" : "TEAMMATE",
				};
			});

		dispatchAnalyticsTrack("Onboarding Invite Issued", {
			feature: "Onboarding",
			...getInviteAnalytics(),
		});

		const captchaToken = await recaptcha("onboarding_invite");

		commitMutation<MutationType>(environment, {
			mutation: InvitePageContactMutation,
			variables: {
				input: {
					invitations,
					captchaToken,
					// eslint-disable-next-line camelcase
					analytics: JSON.stringify({ ux_location: "onboarding__invite_page" }),
				},
			},
			onCompleted: ({ inviteUsers }, err) => {
				if (inviteUsers?.invitees) {
					const { invitees } = inviteUsers;

					const errors = invitees.map(invitee => invitee?.errors?.[0]).filter(error => error);

					if (err && err.length) {
						const errorMessage =
							getFirstUserFacingError(errors) ?? `Something went wrong, and your invitations didn't go out! ${err}`;
						toast.error(errorMessage);
					} else if (errors.length) {
						const error = errors[0];
						if (error) toast.error(`Something went wrong, and your invitations didn't go out! ${error.message}`);
					} else {
						setInvitesSuccess(true);
						window.setTimeout(onNext, 500);
					}
				}
			},
		});
	}, [areValid, getInviteAnalytics, invites, invitesSent, isManager, onNext, isNewOnboardingFlow]);

	const skipInvite = useCallback(() => {
		dispatchAnalyticsTrack("Onboarding Invite Skipped", {
			feature: "Onboarding",
			...getInviteAnalytics(),
		});
		onNext();
	}, [onNext, getInviteAnalytics]);

	const filledInputs = invites.filter(invite => invite.email);
	return (
		<>
			{!isMobile && (
				<div className={styles.avatarsWrapper}>
					<img src={avatarsLeft} className={styles.avatarsLeft} />
					<img src={avatarsRight} className={styles.avatarsRight} />
				</div>
			)}

			<div className={stylesOnboarding.title}>Add your {isManager ? "direct reports" : "teammates"}</div>
			<div className={cx(styles.subtitle, stylesOnboarding.subtitle)}>
				{isManager ? (
					<>
						Add the people you work with to start having more effective{" "}
						{interest === "oneonone" ? "1-on-1s" : "meetings"} today.
					</>
				) : (
					<>
						Add the people you have {interest === "oneonone" ? "1-on-1s" : "meetings"} with to collaborate more
						effectively today.
					</>
				)}
			</div>

			<p className={styles.question}>Who do you have {interest === "oneonone" ? "1-on-1s" : "meetings"} with?</p>

			<div className={styles.inputGroup}>
				{invites.map(invite => (
					<InviteInput
						key={invite.id}
						invite={invite}
						domains={domains}
						regex={regex}
						onTextChange={onInputChange}
						onRemove={removeInput}
					/>
				))}
				<div className={cx(styles.inviteRow, styles.addRow)} onClick={insertInput}>
					<Button size="wide" variant="tertiary" className={styles.addButton}>
						<FontAwesomeIcon icon={faUserPlus} />
						<span>Add another</span>
					</Button>
				</div>
			</div>

			<div className={stylesOnboarding.footer}>
				<Button size="wide" variant="primary" onClick={addTeammates} disabled={!filledInputs.length}>
					{invitesSent ? (
						invitesSuccess ? (
							<>
								<FontAwesomeIcon icon={faCheck} /> Invited
							</>
						) : (
							<FontAwesomeIcon icon={faSpinnerThird} spin />
						)
					) : (
						<>Add {isManager ? "reports" : "teammates"}</>
					)}
				</Button>
				<div style={{ marginTop: "1.5rem" }}>
					<ButtonLink data-test-id="skip-invite" onClick={skipInvite} className={styles.skipInvite}>
						Not now
					</ButtonLink>
				</div>
			</div>
		</>
	);
}

export default function InvitePageRenderer() {
	const props = useLazyLoadQuery<QueryType>(
		graphql`
			query InvitePageQuery {
				contacts(first: 20, ranked: true, excludeUsers: true) {
					edges {
						node {
							email
							image
						}
					}
				}
				viewer {
					account {
						allowInviting
						ssoProvider
					}
					...InvitePage_viewer
				}
			}
		`,
		{},
		{ fetchPolicy: "store-or-network" },
	);

	const { viewer, contacts } = props;
	const { nextStep } = React.useContext(JourneyContext);

	useEffect(() => {
		if (!viewer.account.allowInviting) {
			dispatchAnalyticsTrack("Onboarding Invite Not Allowed", { feature: "Onboarding" });
			nextStep();
		}

		if (viewer.account.ssoProvider) {
			dispatchAnalyticsTrack("Onboarding Invite Skipped (Okta is Enabled)", { feature: "Onboarding" });
			nextStep();
		}
	}, [viewer, nextStep]);

	let invites = compact(contacts?.edges.map(edge => edge?.node))
		.filter(({ email }) => {
			return email && !emailGreylist.test(email);
		})
		.map(({ ...contact }) => ({
			id: uuidv4(),
			...contact,
		}));

	// Use the viewer data
	return <InvitePage invites={invites} viewer={props.viewer} onNext={nextStep} />;
}
